// Copia de nara-ui/src/componentes/CamposContacto.jsx mientras el ERP no use
// nara-ui: si se cambia allá, se cambia aquí.
import { InputAdornment, InputBase, MenuItem, Select, TextField } from '@mui/material';
import { useState } from 'react';

import {
  OTRO, PAISES, errorCorreo, errorTelefono, maxDigitos, paisDe, paisPorDefecto, partirTelefono, soloDigitos,
  unirTelefono,
} from '../contacto';
const tipografia = { tamanos: { base: 14 } };

/**
 * CampoTelefono: teléfono o WhatsApp con indicativo de país (`contacto.js`).
 *
 * El país se escoge a la izquierda («+57»): están todos (los de la región
 * primero) y, al final, «Otro indicativo», que deja escribirlo a mano. En el
 * número solo entran dígitos, hasta los que admite ese país. `alCambiar`
 * recibe el número completo y sin espacios (`+573001234567`), o '' si se
 * borra. Si se pega un número con su indicativo («+57 300 123 4567»), el país
 * se toma de ahí.
 *
 * El aviso de un número que no es válido para su país sale al salir del
 * campo, o siempre con `mostrarError` (cuando se intentó guardar). Los demás
 * props van al TextField.
 */
export function CampoTelefono({
  valor, alCambiar, etiqueta = 'WhatsApp', requerido, mostrarError, ayuda, zona, size = 'small',
  error: errorExterno, helperText, ...resto
}) {
  const inicial = partirTelefono(valor, paisPorDefecto(zona));
  // El país escogido cuenta mientras no hay número; con número, manda el
  // indicativo que trae (al abrir un registro guardado, por ejemplo).
  const [escogido, setEscogido] = useState(inicial.pais);
  const [indOtro, setIndOtro] = useState(inicial.pais === OTRO ? inicial.indicativo : '');
  const [tocado, setTocado] = useState(false);

  const todo = soloDigitos(valor);
  let pais; let digitos;
  if (escogido === OTRO && (!todo || todo.startsWith(indOtro))) {
    pais = OTRO; digitos = todo.slice(indOtro.length);
  } else {
    ({ pais, digitos } = partirTelefono(valor, escogido));
  }
  const tope = maxDigitos(pais);
  const problema = errorTelefono(valor, pais);
  const ver = Boolean(problema) && (tocado || mostrarError);
  const falta = requerido && mostrarError && !digitos;

  const escribir = (texto) => {
    const t = String(texto).trim();
    if (t.startsWith('+') || t.startsWith('00')) {
      const partes = partirTelefono(t, pais);
      setEscogido(partes.pais);
      if (partes.pais === OTRO) setIndOtro(partes.indicativo);
      alCambiar(unirTelefono(partes.pais, partes.digitos.slice(0, maxDigitos(partes.pais)), partes.indicativo));
      return;
    }
    alCambiar(unirTelefono(pais, soloDigitos(t).slice(0, tope), indOtro));
  };
  const cambiarPais = (nuevo) => {
    setEscogido(nuevo);
    alCambiar(unirTelefono(nuevo, digitos, indOtro));
  };
  const cambiarIndicativo = (texto) => {
    const ind = soloDigitos(texto).slice(0, 4);
    setIndOtro(ind);
    alCambiar(unirTelefono(OTRO, digitos, ind));
  };

  return (
    <TextField
      size={size} label={etiqueta + (requerido ? ' *' : '')} value={digitos}
      onChange={(e) => escribir(e.target.value)} onBlur={() => setTocado(true)}
      error={Boolean(errorExterno) || ver || falta}
      helperText={helperText || (ver ? problema : falta ? 'Falta este dato' : ayuda)}
      slotProps={{
        htmlInput: { inputMode: 'numeric', autoComplete: 'tel-national', 'aria-label': etiqueta },
        input: {
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5, gap: 0.25 }}>
              <Select variant="standard" disableUnderline value={pais}
                      onChange={(e) => cambiarPais(e.target.value)}
                      renderValue={(c) => (c === OTRO ? 'Otro +' : `+${paisDe(c).indicativo}`)}
                      inputProps={{ 'aria-label': `País de ${etiqueta.toLowerCase()}` }}
                      sx={{ fontSize: tipografia.tamanos.base, '& .MuiSelect-select': { py: 0, pr: '20px !important' } }}
                      MenuProps={{ slotProps: { paper: { sx: { maxHeight: 360 } } } }}>
                {PAISES.map((x) => (
                  <MenuItem key={x.codigo} value={x.codigo} sx={{ fontSize: tipografia.tamanos.base }}>
                    {x.nombre}
                    {x.indicativo && <span style={{ opacity: 0.6, marginLeft: 6 }}>+{x.indicativo}</span>}
                  </MenuItem>
                ))}
              </Select>
              {pais === OTRO && (
                <InputBase value={indOtro} onChange={(e) => cambiarIndicativo(e.target.value)} placeholder="000"
                           inputProps={{ inputMode: 'numeric', 'aria-label': 'Indicativo', size: 4 }}
                           sx={{ fontSize: tipografia.tamanos.base, width: 44, '& input': { p: 0 } }} />
              )}
            </InputAdornment>
          ),
        },
      }}
      {...resto}
    />
  );
}

/**
 * CampoCorreo: correo electrónico con su validación (`contacto.js`). El aviso
 * sale al salir del campo, o siempre con `mostrarError`.
 */
export function CampoCorreo({
  valor, alCambiar, etiqueta = 'Correo electrónico', requerido, mostrarError, ayuda, size = 'small',
  error: errorExterno, helperText, ...resto
}) {
  const [tocado, setTocado] = useState(false);
  const problema = errorCorreo(valor);
  const ver = Boolean(problema) && (tocado || mostrarError);
  const falta = requerido && mostrarError && !String(valor ?? '').trim();
  return (
    <TextField
      size={size} type="email" label={etiqueta + (requerido ? ' *' : '')} value={valor ?? ''}
      onChange={(e) => alCambiar(e.target.value.replace(/\s/g, ''))} onBlur={() => setTocado(true)}
      error={Boolean(errorExterno) || ver || falta}
      helperText={helperText || (ver ? problema : falta ? 'Falta este dato' : ayuda)}
      slotProps={{ htmlInput: { inputMode: 'email', autoComplete: 'email' } }}
      {...resto}
    />
  );
}
